import { useEffect, useState } from "react"

/** Base do crm derivada do submit de lead (mesma env usada pelos forms). */
const crmBaseUrl = (): string =>
  (process.env.NEXT_PUBLIC_DEFAULT_FORM_SUBMIT_URL || "").replace(
    /\/conversions\/?$/,
    "",
  )

/** Horários disponíveis de uma unidade num dia (crm público, config − vagas). */
export const useAvailability = (
  accountId?: string,
  unitId?: string,
  date?: string,
) => {
  const [slots, setSlots] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    const base = crmBaseUrl()
    if (!accountId || !unitId || !date || !base) {
      setSlots([])
      return
    }
    let cancelled = false
    setLoading(true)
    setError(false)
    const params = new URLSearchParams({ accountId, unitId, date })
    fetch(`${base}/test-drive-availability?${params.toString()}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((res) => {
        if (!cancelled) setSlots(Array.isArray(res?.data) ? res.data : [])
      })
      .catch(() => {
        if (!cancelled) {
          setSlots([])
          setError(true)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [accountId, unitId, date])

  return { slots, loading, error }
}

/** Quais unidades têm agenda aberta na janela (uma passada de fetch por unidade).
    Serve o estado bloqueado da Etapa 1 (nenhuma aberta) e o aviso da Etapa 2 (a
    unidade escolhida fechada). Fail-open: erro de rede não entra em nenhum dos sets. */
export const useUnitsAvailability = (
  accountId?: string,
  unitIds?: string[],
  lookaheadDays = 60,
): { openUnitIds: Set<string>; closedUnitIds: Set<string>; loaded: boolean } => {
  const key = (unitIds ?? []).join(",")
  const [state, setState] = useState<{
    openUnitIds: Set<string>
    closedUnitIds: Set<string>
    loaded: boolean
  }>({ openUnitIds: new Set(), closedUnitIds: new Set(), loaded: false })

  useEffect(() => {
    const base = crmBaseUrl()
    const ids = (unitIds ?? []).filter(Boolean)
    if (!accountId || !base || ids.length === 0) {
      setState({ openUnitIds: new Set(), closedUnitIds: new Set(), loaded: false })
      return
    }
    let cancelled = false
    const pad = (n: number) => String(n).padStart(2, "0")
    const iso = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    const from = new Date()
    from.setDate(from.getDate() + 1)
    const to = new Date(from)
    to.setDate(to.getDate() + lookaheadDays)
    const fromISO = iso(from)
    const toISO = iso(to)

    Promise.all(
      ids.map((unitId) => {
        const params = new URLSearchParams({
          accountId,
          unitId,
          from: fromISO,
          to: toISO,
        })
        return fetch(`${base}/test-drive-availability/days?${params.toString()}`)
          .then((r) =>
            r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)),
          )
          .then((res) => ({
            unitId,
            status: (Array.isArray(res?.data) && res.data.length > 0
              ? "open"
              : "empty") as "open" | "empty",
          }))
          .catch(() => ({ unitId, status: "error" as const }))
      }),
    ).then((results) => {
      if (cancelled) return
      setState({
        openUnitIds: new Set(
          results.filter((r) => r.status === "open").map((r) => r.unitId),
        ),
        closedUnitIds: new Set(
          results.filter((r) => r.status === "empty").map((r) => r.unitId),
        ),
        loaded: true,
      })
    })

    return () => {
      cancelled = true
    }
  }, [accountId, key, lookaheadDays])

  return state
}

/** Dias com atendimento de uma unidade num intervalo (crm público) — usado para
    desabilitar os dias fechados no calendário (o que o cliente configurou). */
export const useAvailableDays = (
  accountId?: string,
  unitId?: string,
  from?: string,
  to?: string,
): { days: Set<string>; loaded: boolean } => {
  const [state, setState] = useState<{ days: Set<string>; loaded: boolean }>({
    days: new Set(),
    loaded: false,
  })

  useEffect(() => {
    const base = crmBaseUrl()
    if (!accountId || !unitId || !from || !to || !base) {
      setState({ days: new Set(), loaded: false })
      return
    }
    let cancelled = false
    const params = new URLSearchParams({ accountId, unitId, from, to })
    fetch(`${base}/test-drive-availability/days?${params.toString()}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((res) => {
        // Carregado (mesmo vazio) → restringe o calendário aos dias abertos.
        if (!cancelled)
          setState({
            days: new Set(Array.isArray(res?.data) ? (res.data as string[]) : []),
            loaded: true,
          })
      })
      .catch(() => {
        // Falha → não restringe (só a regra de data mínima vale).
        if (!cancelled) setState({ days: new Set(), loaded: false })
      })
    return () => {
      cancelled = true
    }
  }, [accountId, unitId, from, to])

  return state
}
